import React, { useState, useEffect, useRef } from 'react';
import DonationSidebar from '../components/DonationSidebar';
import { loadAllSurahs, QuranSurah } from '../data/quran';

// Audio Player Component
const AudioPlayer: React.FC<{ audioUrl: string }> = ({ audioUrl }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const togglePlay = async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        setIsLoading(true);
        setError(null);
        await audioRef.current.play();
        setIsPlaying(true);
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error playing audio:', err);
      setError('Failed to play audio');
      setIsLoading(false);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setIsLoading(false);
  };

  const handleError = () => {
    setError('Audio not available');
    setIsLoading(false);
    setIsPlaying(false);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <audio
        ref={audioRef}
        onEnded={handleEnded}
        onPause={() => setIsPlaying(false)}
        onError={handleError}
        preload="none"
      >
        <source src={audioUrl} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
      
      <button
        onClick={togglePlay}
        disabled={isLoading}
        style={{
          background: 'none',
          border: 'none',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          fontSize: '20px',
          color: error ? 'var(--text-secondary)' : 'var(--accent-primary)',
          padding: '4px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isLoading ? 0.6 : 1
        }}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isLoading ? '⏳' : isPlaying ? '⏸️' : '▶️'}
      </button>
      
      <span style={{ 
        fontSize: '12px', 
        color: error ? 'var(--text-secondary)' : 'var(--text-secondary)' 
      }}>
        {error ? 'Audio NA' : 'Listen'}
      </span>
    </div>
  );
};

// Juz Navigation Component
const JuzNavigation: React.FC<{ 
  onJuzSelect: (juz: any) => void,
  onClose: () => void 
}> = ({ onJuzSelect, onClose }) => {
  const [juzData, setJuzData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadJuzData = async () => {
      try {
        const response = await fetch('/data/quran/juz.json');
        const data = await response.json();
        setJuzData(data);
      } catch (error) {
        console.error('Error loading Juz data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadJuzData();
  }, []);

  if (loading) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <p>Loading Juz data...</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h3>Navigate by Juz (Part)</h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            color: 'var(--text-secondary)'
          }}
        >
          ✕
        </button>
      </div>
      
      <div className="grid-5">
        {juzData.map(juz => (
          <button
            key={juz.index}
            onClick={() => onJuzSelect(juz)}
            style={{
              padding: '12px 8px',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              backgroundColor: 'var(--bg-primary)',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ 
              fontSize: '16px', 
              fontWeight: 'bold',
              color: 'var(--accent-primary)'
            }}>
              Juz {juz.index}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: 'var(--text-secondary)',
              marginTop: '4px'
            }}>
              {juz.start.name}
              {juz.start.name !== juz.end.name && ` - ${juz.end.name}`}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const Quran: React.FC = () => {
  const [selectedSurah, setSelectedSurah] = useState<QuranSurah | null>(null);
  const [surahs, setSurahs] = useState<QuranSurah[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'quran' | 'names'>('quran');
  const [showTajweed, setShowTajweed] = useState(false);
  const [showJuzNavigation, setShowJuzNavigation] = useState(false);
  const [tajweedData, setTajweedData] = useState<any>(null);
  const [filteredSurahs, setFilteredSurahs] = useState<QuranSurah[]>([]);
  const [selectedJuz, setSelectedJuz] = useState<any>(null);
  const [juzData, setJuzData] = useState<any[]>([]);

  // Tajweed color mapping
  const tajweedColors = {
    'madd_2': '#FF6B6B',     // Red for elongation
    'madd_246': '#4ECDC4',   // Teal for medium madd
    'madd_6': '#45B7D1',     // Blue for long madd
    'hamzat_wasl': '#96CEB4', // Green for connecting hamza
    'lam_shamsiyyah': '#FFEAA7', // Yellow for sun letters
    'default': '#FFA726'     // Orange for other rules
  };

  useEffect(() => {
    const initializeQuran = async () => {
      try {
        setLoading(true);
        setError(null);
        const allSurahs = await loadAllSurahs();

        if (allSurahs.length === 0) {
          throw new Error('No surahs loaded. Please check your data files.');
        }

        setSurahs(allSurahs);
        setFilteredSurahs(allSurahs);

        // Load Juz data
        const juzResponse = await fetch('/data/quran/juz.json');
        const juzData = await juzResponse.json();
        setJuzData(juzData);
      } catch (error) {
        console.error('Error loading Quran data:', error);
        setError('Failed to load Quran data. Please check the console for details.');
      } finally {
        setLoading(false);
      }
    };

    initializeQuran();
  }, []);

  // Load tajweed data when surah is selected
  useEffect(() => {
    if (selectedSurah) {
      const loadTajweedData = async () => {
        try {
          // Convert number to string and pad with leading zeros
          const surahNumber = selectedSurah.number.toString().padStart(3, '0');
          const response = await fetch(`/data/quran/tajweed/surah_${surahNumber}.json`);
          if (response.ok) {
            const data = await response.json();
            setTajweedData(data);
          } else {
            setTajweedData(null);
          }
        } catch (error) {
          console.error('Error loading tajweed data:', error);
          setTajweedData(null);
        }
      };

      loadTajweedData();
    }
  }, [selectedSurah]);

  // Filter surahs based on search term or selected Juz
  useEffect(() => {
    let filtered = surahs;

    if (selectedJuz) {
      // Filter surahs based on selected Juz
      const startSurahNum = parseInt(selectedJuz.start.index);
      const endSurahNum = parseInt(selectedJuz.end.index);
      
      filtered = surahs.filter(surah => {
        const surahNum = surah.number;
        return surahNum >= startSurahNum && surahNum <= endSurahNum;
      });
    }

    if (searchTerm) {
      filtered = filtered.filter((surah) =>
        (surah.transliteration || '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (surah.englishNameTranslation || '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (surah.name || '').includes(searchTerm)
      );
    }

    setFilteredSurahs(filtered);
  }, [searchTerm, surahs, selectedJuz]);

  const handleJuzSelect = (juz: any) => {
    setSelectedJuz(juz);
    setShowJuzNavigation(false);
    setSearchTerm(''); // Clear search when Juz is selected
  };

  const clearJuzFilter = () => {
    setSelectedJuz(null);
  };

  const getJuzSurahRange = (juz: any) => {
    if (!juz) return '';
    const startSurahNum = parseInt(juz.start.index);
    const endSurahNum = parseInt(juz.end.index);
    
    if (startSurahNum === endSurahNum) {
      return `Surah ${startSurahNum}`;
    } else {
      return `Surahs ${startSurahNum} - ${endSurahNum}`;
    }
  };

  const renderTajweedText = (ayahText: string, ayahNumber: number) => {
    if (!showTajweed || !tajweedData) return ayahText;

    const verseKey = `verse_${ayahNumber}`;
    const rules = tajweedData.verse[verseKey];
    
    if (!rules) return ayahText;

    let result = [];
    let lastIndex = 0;
    
    // Sort rules by start position
    const sortedRules = [...rules].sort((a, b) => a.start - b.start);
    
    sortedRules.forEach(rule => {
      // Add text before the rule
      if (rule.start > lastIndex) {
        result.push(ayahText.slice(lastIndex, rule.start));
      }
      
      // Add colored rule text
      const ruleText = ayahText.slice(rule.start, rule.end + 1);
      result.push(
        <span 
          key={`${rule.start}-${rule.end}`} 
          style={{ 
            backgroundColor: tajweedColors[rule.rule as keyof typeof tajweedColors] || tajweedColors.default,
            color: '#000',
            padding: '1px 2px',
            borderRadius: '3px',
            margin: '0 1px',
            fontWeight: 'bold'
          }}
          title={rule.rule}
        >
          {ruleText}
        </span>
      );
      
      lastIndex = rule.end + 1;
    });
    
    // Add remaining text
    if (lastIndex < ayahText.length) {
      result.push(ayahText.slice(lastIndex));
    }
    
    return result;
  };

  const namesOfAllah = [
    { number: 1, arabic: 'ٱلرَّحْمَٰنُ', transliteration: 'Ar-Raḥmān', meaning: 'The Most or Entirely Merciful' },
    { number: 2, arabic: 'ٱلرَّحِيمُ', transliteration: 'Ar-Raḥīm', meaning: 'The Especially Merciful' },
    { number: 3, arabic: 'ٱلْمَلِكُ', transliteration: 'Al-Malik', meaning: 'The King and Owner of Dominion' },
    { number: 4, arabic: 'ٱلْقُدُّوسُ', transliteration: 'Al-Quddūs', meaning: 'The Absolutely Pure' },
    { number: 5, arabic: 'ٱلسَّلَامُ', transliteration: 'As-Salām', meaning: 'The Perfection and Giver of Peace' },
    { number: 6, arabic: 'ٱلْمُؤْمِنُ', transliteration: 'Al-Mu\'min', meaning: 'The One Who gives Emaan and Security' },
    { number: 7, arabic: 'ٱلْمُهَيْمِنُ', transliteration: 'Al-Muhaymin', meaning: 'The Guardian, The Witness, The Overseer' },
    { number: 8, arabic: 'ٱلْعَزِيزُ', transliteration: 'Al-\'Azīz', meaning: 'The Almighty' },
    { number: 9, arabic: 'ٱلْجَبَّارُ', transliteration: 'Al-Jabbār', meaning: 'The Compeller, The Restorer' },
    { number: 10, arabic: 'ٱلْمُتَكَبِّرُ', transliteration: 'Al-Mutakabbir', meaning: 'The Supreme, The Majestic' },
    { number: 11, arabic: 'ٱلْخَالِقُ', transliteration: 'Al-Khāliq', meaning: 'The Creator, The Maker' },
    { number: 12, arabic: 'ٱلْبَارِئُ', transliteration: 'Al-Bāri\'', meaning: 'The Originator' },
    { number: 13, arabic: 'ٱلْمُصَوِّرُ', transliteration: 'Al-Muṣawwir', meaning: 'The Fashioner' },
    { number: 14, arabic: 'ٱلْغَفَّارُ', transliteration: 'Al-Ghaffār', meaning: 'The Constant Forgiver' },
    { number: 15, arabic: 'ٱلْقَهَّارُ', transliteration: 'Al-Qahhār', meaning: 'The Subduer, The Ever-Dominating' },
    { number: 16, arabic: 'ٱلْوَهَّابُ', transliteration: 'Al-Wahhāb', meaning: 'The Giver of Gifts' },
    { number: 17, arabic: 'ٱلْرَّزَّاقُ', transliteration: 'Ar-Razzāq', meaning: 'The Ever-Providing' },
    { number: 18, arabic: 'ٱلْفَتَّاحُ', transliteration: 'Al-Fattāḥ', meaning: 'The Opener, The Judge' },
    { number: 19, arabic: 'ٱلْعَلِيمُ', transliteration: 'Al-\'Alīm', meaning: 'The All-Knowing, The Omniscient' },
    { number: 20, arabic: 'ٱلْقَابِضُ', transliteration: 'Al-Qābiḍ', meaning: 'The Withholder' },
    { number: 21, arabic: 'ٱلْبَاسِطُ', transliteration: 'Al-Bāsiṭ', meaning: 'The Extender' },
    { number: 22, arabic: 'ٱلْخَافِضُ', transliteration: 'Al-Khāfiḍ', meaning: 'The Reducer, The Abaser' },
    { number: 23, arabic: 'ٱلْرَّافِعُ', transliteration: 'Ar-Rāfi\'', meaning: 'The Exalter' },
    { number: 24, arabic: 'ٱلْمُعِزُّ', transliteration: 'Al-Mu\'izz', meaning: 'The Honourer' },
    { number: 25, arabic: 'ٱلْمُذِلُّ', transliteration: 'Al-Mudhill', meaning: 'The Dishonourer, The Humiliator' },
    { number: 26, arabic: 'ٱلسَّمِيعُ', transliteration: 'As-Samī\'', meaning: 'The All-Hearing' },
    { number: 27, arabic: 'ٱلْبَصِيرُ', transliteration: 'Al-Baṣīr', meaning: 'The All-Seeing' },
    { number: 28, arabic: 'ٱلْحَكَمُ', transliteration: 'Al-Ḥakam', meaning: 'The Judge, The Giver of Justice' },
    { number: 29, arabic: 'ٱلْعَدْلُ', transliteration: 'Al-\'Adl', meaning: 'The Utterly Just' },
    { number: 30, arabic: 'ٱللَّطِيفُ', transliteration: 'Al-Laṭīf', meaning: 'The Subtle One, The Most Gentle' },
    { number: 31, arabic: 'ٱلْخَبِيرُ', transliteration: 'Al-Khabīr', meaning: 'The Acquainted, The All-Aware' },
    { number: 32, arabic: 'ٱلْحَلِيمُ', transliteration: 'Al-Ḥalīm', meaning: 'The Most Forbearing' },
    { number: 33, arabic: 'ٱلْعَظِيمُ', transliteration: 'Al-\'Aẓīm', meaning: 'The Magnificent, The Supreme' },
    { number: 34, arabic: 'ٱلْغَفُورُ', transliteration: 'Al-Ghafūr', meaning: 'The Forgiving, The Exceedingly Forgiving' },
    { number: 35, arabic: 'ٱلشَّكُورُ', transliteration: 'Ash-Shakūr', meaning: 'The Most Appreciative' },
    { number: 36, arabic: 'ٱلْعَلِيُّ', transliteration: 'Al-\'Alī', meaning: 'The Most High, The Exalted' },
    { number: 37, arabic: 'ٱلْكَبِيرُ', transliteration: 'Al-Kabīr', meaning: 'The Greatest, The Most Grand' },
    { number: 38, arabic: 'ٱلْحَفِيظُ', transliteration: 'Al-Ḥafīẓ', meaning: 'The Preserver, The All-Heedful and All-Protecting' },
    { number: 39, arabic: 'ٱلْمُقِيتُ', transliteration: 'Al-Muqīt', meaning: 'The Sustainer, The Maintainer' },
    { number: 40, arabic: 'ٱلْحَسِيبُ', transliteration: 'Al-Ḥasīb', meaning: 'The Reckoner' },
    { number: 41, arabic: 'ٱلْجَلِيلُ', transliteration: 'Al-Jalīl', meaning: 'The Majestic' },
    { number: 42, arabic: 'ٱلْكَرِيمُ', transliteration: 'Al-Karīm', meaning: 'The Most Generous, The Most Esteemed' },
    { number: 43, arabic: 'ٱلْرَّقِيبُ', transliteration: 'Ar-Raqīb', meaning: 'The Watchful, The All-Watchful' },
    { number: 44, arabic: 'ٱلْمُجِيبُ', transliteration: 'Al-Mujīb', meaning: 'The Responsive, The Answerer' },
    { number: 45, arabic: 'ٱلْوَاسِعُ', transliteration: 'Al-Wāsi\'', meaning: 'The All-Encompassing, the Boundless' },
    { number: 46, arabic: 'ٱلْحَكِيمُ', transliteration: 'Al-Ḥakīm', meaning: 'The All-Wise' },
    { number: 47, arabic: 'ٱلْوَدُودُ', transliteration: 'Al-Wadūd', meaning: 'The Most Loving' },
    { number: 48, arabic: 'ٱلْمَجِيدُ', transliteration: 'Al-Majīd', meaning: 'The Glorious, The Most Honorable' },
    { number: 49, arabic: 'ٱلْبَاعِثُ', transliteration: 'Al-Bā\'ith', meaning: 'The Infuser of New Life' },
    { number: 50, arabic: 'ٱلشَّهِيدُ', transliteration: 'Ash-Shahīd', meaning: 'The All-Witnessing' },
    { number: 51, arabic: 'ٱلْحَقُّ', transliteration: 'Al-Ḥaqq', meaning: 'The Absolute Truth' },
    { number: 52, arabic: 'ٱلْوَكِيلُ', transliteration: 'Al-Wakīl', meaning: 'The Trustee, The Disposer of Affairs' },
    { number: 53, arabic: 'ٱلْقَوِيُّ', transliteration: 'Al-Qawiyy', meaning: 'The All-Strong' },
    { number: 54, arabic: 'ٱلْمَتِينُ', transliteration: 'Al-Matīn', meaning: 'The Firm, The Steadfast' },
    { number: 55, arabic: 'ٱلْوَلِيُّ', transliteration: 'Al-Walī', meaning: 'The Protecting Associate' },
    { number: 56, arabic: 'ٱلْحَمِيدُ', transliteration: 'Al-Ḥamīd', meaning: 'The Praiseworthy' },
    { number: 57, arabic: 'ٱلْمُحْصِي', transliteration: 'Al-Muḥṣī', meaning: 'The All-Enumerating, The Counter' },
    { number: 58, arabic: 'ٱلْمُبْدِئُ', transliteration: 'Al-Mubdi\'', meaning: 'The Originator, The Initiator' },
    { number: 59, arabic: 'ٱلْمُعِيدُ', transliteration: 'Al-Mu\'īd', meaning: 'The Restorer, The Reinstater' },
    { number: 60, arabic: 'ٱلْمُحْيِي', transliteration: 'Al-Muḥyī', meaning: 'The Giver of Life' },
    { number: 61, arabic: 'ٱلْمُمِيتُ', transliteration: 'Al-Mumīt', meaning: 'The Creator of Death' },
    { number: 62, arabic: 'ٱلْحَيُّ', transliteration: 'Al-Ḥayy', meaning: 'The Ever-Living' },
    { number: 63, arabic: 'ٱلْقَيُّومُ', transliteration: 'Al-Qayyūm', meaning: 'The Sustainer, The Self-Subsisting' },
    { number: 64, arabic: 'ٱلْوَاجِدُ', transliteration: 'Al-Wājid', meaning: 'The Perceiver' },
    { number: 65, arabic: 'ٱلْمَاجِدُ', transliteration: 'Al-Mājid', meaning: 'The Illustrious, The Magnificent' },
    { number: 66, arabic: 'ٱلْوَاحِدُ', transliteration: 'Al-Wāḥid', meaning: 'The One, The Indivisible' },
    { number: 67, arabic: 'ٱلْأَحَدُ', transliteration: 'Al-Aḥad', meaning: 'The Unique, The Only One' },
    { number: 68, arabic: 'ٱلصَّمَدُ', transliteration: 'Aṣ-Ṣamad', meaning: 'The Eternal, Satisfier of Needs' },
    { number: 69, arabic: 'ٱلْقَادِرُ', transliteration: 'Al-Qādir', meaning: 'The Omnipotent' },
    { number: 70, arabic: 'ٱلْمُقْتَدِرُ', transliteration: 'Al-Muqtadir', meaning: 'The Powerful' },
    { number: 71, arabic: 'ٱلْمُقَدِّمُ', transliteration: 'Al-Muqaddim', meaning: 'The Expediter, The Promoter' },
    { number: 72, arabic: 'ٱلْمُؤَخِّرُ', transliteration: 'Al-Mu\'akhkhir', meaning: 'The Delayer' },
    { number: 73, arabic: 'ٱلْأَوَّلُ', transliteration: 'Al-Awwal', meaning: 'The First' },
    { number: 74, arabic: 'ٱلْآخِرُ', transliteration: 'Al-Ākhir', meaning: 'The Last, The Utmost' },
    { number: 75, arabic: 'ٱلظَّاهِرُ', transliteration: 'Aẓ-Ẓāhir', meaning: 'The Manifest, The All-Surpassing' },
    { number: 76, arabic: 'ٱلْبَاطِنُ', transliteration: 'Al-Bāṭin', meaning: 'The Hidden One, Knower of the Hidden' },
    { number: 77, arabic: 'ٱلْوَالِي', transliteration: 'Al-Wālī', meaning: 'The Sole Governor' },
    { number: 78, arabic: 'ٱلْمُتَعَالِي', transliteration: 'Al-Muta\'ālī', meaning: 'The Self-Exalted' },
    { number: 79, arabic: 'ٱلْبَرُّ', transliteration: 'Al-Barr', meaning: 'The Source of All Goodness' },
    { number: 80, arabic: 'ٱلتَّوَّابُ', transliteration: 'At-Tawwāb', meaning: 'The Ever-Pardoning' },
    { number: 81, arabic: 'ٱلْمُنْتَقِمُ', transliteration: 'Al-Muntaqim', meaning: 'The Avenger' },
    { number: 82, arabic: 'ٱلْعَفُوُّ', transliteration: 'Al-\'Afūw', meaning: 'The Pardoner' },
    { number: 83, arabic: 'ٱلْرَّؤُوفُ', transliteration: 'Ar-Ra\'ūf', meaning: 'The Most Kind' },
    { number: 84, arabic: 'مَالِكُ ٱلْمُلْكِ', transliteration: 'Mālik al-Mulk', meaning: 'Master of the Dominion, Owner of the Kingdom' },
    { number: 85, arabic: 'ذُو ٱلْجَلَالِ وَٱلْإِكْرَامِ', transliteration: 'Dhū al-Jalāli wa\'l-Ikrām', meaning: 'Possessor of Glory and Honour' },
    { number: 86, arabic: 'ٱلْمُقْسِطُ', transliteration: 'Al-Muqsiṭ', meaning: 'The Just One' },
    { number: 87, arabic: 'ٱلْجَامِعُ', transliteration: 'Al-Jāmi\'', meaning: 'The Gatherer, the Uniter' },
    { number: 88, arabic: 'ٱلْغَنِيُّ', transliteration: 'Al-Ghanī', meaning: 'The Self-Sufficient, The Wealthy' },
    { number: 89, arabic: 'ٱلْمُغْنِي', transliteration: 'Al-Mughnī', meaning: 'The Enricher' },
    { number: 90, arabic: 'ٱلْمَانِعُ', transliteration: 'Al-Māni\'', meaning: 'The Withholder' },
    { number: 91, arabic: 'ٱلضَّارُّ', transliteration: 'Aḍ-Ḍārr', meaning: 'The Distresser' },
    { number: 92, arabic: 'ٱلنَّافِعُ', transliteration: 'An-Nāfi\'', meaning: 'The Propitious, The Benefactor' },
    { number: 93, arabic: 'ٱلْنُورُ', transliteration: 'An-Nūr', meaning: 'The Light' },
    { number: 94, arabic: 'ٱلْهَادِي', transliteration: 'Al-Hādī', meaning: 'The Guide' },
    { number: 95, arabic: 'ٱلْبَدِيعُ', transliteration: 'Al-Badī\'', meaning: 'The Incomparable Originator' },
    { number: 96, arabic: 'ٱلْبَاقِي', transliteration: 'Al-Bāqī', meaning: 'The Ever-Lasting' },
    { number: 97, arabic: 'ٱلْوَارِثُ', transliteration: 'Al-Wārith', meaning: 'The Inheritor' },
    { number: 98, arabic: 'ٱلرَّشِيدُ', transliteration: 'Ar-Rashīd', meaning: 'The Guide to the Right Path' },
    { number: 99, arabic: 'ٱلصَّبُورُ', transliteration: 'Aṣ-Ṣabūr', meaning: 'The Patient' }
  ];

  if (loading) {
    return (
      <div className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📖</div>
            <p>Loading Holy Quran...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-padding">
      <div className="container">
        <div className="section-title">
          <h1>Holy Quran & Names of Allah</h1>
          <p>Read the words of Allah and learn His beautiful names and attributes</p>
        </div>

        {/* Navigation Tabs */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '40px',
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <button
            onClick={() => setActiveTab('quran')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeTab === 'quran' ? 'var(--accent-primary)' : 'transparent',
              color: activeTab === 'quran' ? 'var(--bg-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: activeTab === 'quran' ? '2px solid var(--accent-primary)' : 'none',
              borderRadius: '8px 8px 0 0'
            }}
          >
            📖 Quran Surahs
          </button>
          <button
            onClick={() => setActiveTab('names')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeTab === 'names' ? 'var(--accent-primary)' : 'transparent',
              color: activeTab === 'names' ? 'var(--bg-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: activeTab === 'names' ? '2px solid var(--accent-primary)' : 'none',
              borderRadius: '8px 8px 0 0'
            }}
          >
            🌟 99 Names of Allah
          </button>
        </div>

        {error && (
          <div
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'center',
              color: 'var(--text-secondary)',
            }}
          >
            {error}
            <div style={{ marginTop: '8px', fontSize: '14px' }}>
              Make sure your Quran data files are in the <code>public/data/quran/</code> folder
            </div>
          </div>
        )}

        {activeTab === 'names' ? (
          /* 99 Names of Allah Section */
          <div>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>Asma-ul-Husna (The Beautiful Names of Allah)</h2>
              <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                Prophet Muhammad (ﷺ) said: "Allah has ninety-nine names, one hundred minus one, and whoever knows them will enter Paradise." (Bukhari)
              </p>
            </div>

            <div style={{
              backgroundColor: 'var(--bg-secondary)',
              padding: '24px',
              borderRadius: '12px',
              marginBottom: '32px',
              border: '1px solid var(--border)'
            }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '16px', textAlign: 'center' }}>
                Quranic Foundation
              </h3>
              <div style={{ color: 'var(--text-secondary)', lineHeight: '1.7', textAlign: 'center' }}>
                <p><em>"And to Allah belong the best names, so invoke Him by them."</em> (Quran 7:180)</p>
                <p><em>"Allah—there is no deity except Him. To Him belong the best names."</em> (Quran 20:8)</p>
              </div>
            </div>

            <div className="grid-3">
              {namesOfAllah.map((name) => (
                <div key={name.number} className="card" style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--accent-primary)',
                    marginBottom: '8px',
                    fontWeight: '600'
                  }}>
                    {name.number}
                  </div>
                  
                  <div className="arabic" style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    marginBottom: '12px',
                    lineHeight: '1.4',
                    direction: 'rtl',
                    fontFamily: "'Amiri', serif"
                  }}>
                    {name.arabic}
                  </div>

                  <div style={{
                    color: 'var(--accent-primary)',
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    {name.transliteration}
                  </div>

                  <p style={{
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    {name.meaning}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : !selectedSurah ? (
          /* Quran Surahs List */
          <>
            {/* Search and Navigation Bar */}
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              alignItems: 'center', 
              marginBottom: '40px',
              flexWrap: 'wrap'
            }}>
              <div style={{ flex: '1', minWidth: '300px' }}>
                <input
                  type="text"
                  placeholder="Search Surah by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: '16px',
                  }}
                />
              </div>
              
              <button
                onClick={() => setShowJuzNavigation(!showJuzNavigation)}
                style={{
                  padding: '12px 20px',
                  border: '1px solid var(--accent-primary)',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--accent-primary)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap'
                }}
              >
                📚 Navigate by Juz
              </button>
            </div>

            {/* Juz Navigation */}
            {showJuzNavigation && (
              <div style={{ marginBottom: '32px' }}>
                <JuzNavigation 
                  onJuzSelect={handleJuzSelect}
                  onClose={() => setShowJuzNavigation(false)}
                />
              </div>
            )}

            {/* Juz Filter Indicator */}
            {selectedJuz && (
              <div style={{
                backgroundColor: 'var(--bg-secondary)',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px',
                border: '1px solid var(--accent-primary)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong style={{ color: 'var(--accent-primary)' }}>
                    Viewing Juz {selectedJuz.index}: {getJuzSurahRange(selectedJuz)}
                  </strong>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    {selectedJuz.start.name} to {selectedJuz.end.name}
                  </div>
                </div>
                <button
                  onClick={clearJuzFilter}
                  style={{
                    background: 'none',
                    border: '1px solid var(--text-secondary)',
                    color: 'var(--text-secondary)',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Show All Surahs
                </button>
              </div>
            )}

            {/* Surah List */}
            <div className="grid-3">
              {filteredSurahs.map((surah) => (
                <div
                  key={surah.number}
                  className="card"
                  style={{
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => setSelectedSurah(surah)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div
                    style={{
                      fontSize: '14px',
                      color: 'var(--accent-primary)',
                      marginBottom: '6px',
                      fontWeight: '600',
                    }}
                  >
                    {surah.number}.
                  </div>

                  <div
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      marginBottom: '6px',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {surah.transliteration}
                  </div>

                  <div
                    className="arabic"
                    style={{
                      fontSize: '22px',
                      fontWeight: 'bold',
                      lineHeight: '1.4',
                      marginBottom: '6px',
                      direction: 'rtl',
                      fontFamily: "'Amiri', serif",
                    }}
                  >
                    {surah.name}
                  </div>

                  <p
                    style={{
                      color: 'var(--text-secondary)',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontStyle: 'italic',
                    }}
                  >
                    {surah.englishNameTranslation}
                  </p>

                  <div
                    style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      marginTop: '8px',
                      backgroundColor: 'var(--bg-secondary)',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      display: 'inline-block',
                    }}
                  >
                    {surah.count} Ayahs • {surah.revelationType}
                  </div>
                </div>
              ))}
            </div>

            {searchTerm && filteredSurahs.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  marginTop: '40px',
                }}
              >
                <p>No surahs found matching "{searchTerm}"</p>
              </div>
            )}

            {selectedJuz && filteredSurahs.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  marginTop: '40px',
                }}
              >
                <p>No surahs found in Juz {selectedJuz.index}</p>
              </div>
            )}
          </>
        ) : (
          /* Individual Surah View */
          <>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '24px',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <button
                onClick={() => setSelectedSurah(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent-primary)',
                  cursor: 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--bg-secondary)',
                }}
              >
                ← Back to {selectedJuz ? `Juz ${selectedJuz.index}` : 'Surah List'}
              </button>

              {/* Tajweed Toggle */}
              <button
                onClick={() => setShowTajweed(!showTajweed)}
                style={{
                  padding: '8px 16px',
                  border: `1px solid ${showTajweed ? 'var(--accent-primary)' : 'var(--border)'}`,
                  backgroundColor: showTajweed ? 'var(--accent-primary)' : 'var(--bg-primary)',
                  color: showTajweed ? 'white' : 'var(--text-primary)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {showTajweed ? '🔴 Hide Tajweed' : '📖 Show Tajweed'}
              </button>
            </div>

            {/* Tajweed Legend */}
            {showTajweed && (
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '12px', 
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '8px',
                border: '1px solid var(--border)'
              }}>
                <div style={{ fontSize: '14px', fontWeight: '600', width: '100%' }}>
                  Tajweed Rules Legend:
                </div>
                {Object.entries(tajweedColors).map(([rule, color]) => (
                  <div key={rule} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    fontSize: '12px'
                  }}>
                    <div style={{ 
                      width: '16px', 
                      height: '16px', 
                      backgroundColor: color,
                      borderRadius: '3px',
                      border: '1px solid var(--border)'
                    }}></div>
                    <span>{rule.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Surah Header */}
            <div className="card" style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div
                style={{
                  fontSize: '14px',
                  color: 'var(--accent-primary)',
                  marginBottom: '8px',
                  fontWeight: '600',
                }}
              >
                Surah {selectedSurah.number} • {selectedSurah.revelationType}
              </div>

              <div
                className="arabic"
                style={{
                  fontSize: '32px',
                  marginBottom: '12px',
                  fontWeight: 'bold',
                  lineHeight: '1.4',
                  direction: 'rtl',
                  fontFamily: "'Amiri', serif",
                }}
              >
                {selectedSurah.name}
              </div>

              <h1
                style={{
                  marginBottom: '8px',
                  fontSize: '26px',
                  color: 'var(--text-primary)',
                }}
              >
                {selectedSurah.transliteration}
              </h1>

              <p
                style={{
                  color: 'var(--text-secondary)',
                  fontSize: '18px',
                  marginBottom: '16px',
                }}
              >
                {selectedSurah.englishNameTranslation}
              </p>

              <div
                style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  backgroundColor: 'var(--bg-secondary)',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  display: 'inline-block',
                }}
              >
                {selectedSurah.count} Ayahs
              </div>
            </div>

            {/* Ayahs with Translations, Audio, and Tajweed */}
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
              {selectedSurah.ayahs.map((ayah) => (
                <div
                  key={ayah.number}
                  className="card"
                  style={{
                    marginBottom: '32px',
                    position: 'relative',
                  }}
                >
                  {/* Ayah Header with Number and Audio */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '20px',
                      paddingBottom: '12px',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: 'var(--accent-primary)',
                        color: 'var(--bg-primary)',
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        flexShrink: 0,
                      }}
                    >
                      {ayah.numberInSurah}
                    </div>
                    
                    {/* Audio Player */}
                    {ayah.audioUrl && (
                      <AudioPlayer audioUrl={ayah.audioUrl} />
                    )}
                  </div>

                  {/* 1. Original Arabic Text with Tajweed */}
                  <div
                    className="arabic"
                    style={{
                      fontSize: '28px',
                      lineHeight: '2',
                      marginBottom: '24px',
                      textAlign: 'right',
                      direction: 'rtl',
                      fontFamily: "'Amiri', serif",
                      borderBottom: '1px solid var(--border)',
                      paddingBottom: '20px',
                    }}
                  >
                    {renderTajweedText(ayah.text, ayah.numberInSurah)}
                    <div
                      style={{
                        fontSize: '14px',
                        color: 'var(--accent-primary)',
                        marginTop: '8px',
                        textAlign: 'center',
                      }}
                    >
                      ﴿{ayah.numberInSurah}﴾
                    </div>
                  </div>

                  {/* 2. Arabic Translation (Tafsir/Explanation) */}
                  {ayah.arabicTranslation && (
                    <div
                      className="arabic"
                      style={{
                        fontSize: '18px',
                        lineHeight: '1.8',
                        marginBottom: '20px',
                        textAlign: 'right',
                        direction: 'rtl',
                        fontFamily: "'Amiri', serif",
                        color: 'var(--text-secondary)',
                        backgroundColor: 'var(--bg-secondary)',
                        padding: '16px',
                        borderRadius: '8px',
                        borderRight: '3px solid var(--accent-primary)',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '12px',
                          color: 'var(--accent-primary)',
                          marginBottom: '8px',
                          fontWeight: '600',
                        }}
                      >
                        التفسير العربي:
                      </div>
                      {ayah.arabicTranslation}
                    </div>
                  )}

                  {/* 3. English Translation */}
                  {ayah.englishTranslation && (
                    <div
                      style={{
                        fontSize: '16px',
                        lineHeight: '1.6',
                        color: 'var(--text-secondary)',
                        backgroundColor: 'var(--bg-secondary)',
                        padding: '16px',
                        borderRadius: '8px',
                        borderLeft: '3px solid var(--accent-primary)',
                        fontStyle: 'italic',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '12px',
                          color: 'var(--accent-primary)',
                          marginBottom: '8px',
                          fontWeight: '600',
                        }}
                      >
                        English Translation:
                      </div>
                      {ayah.englishTranslation}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <DonationSidebar />
      </div>
    </div>
  );
};

export default Quran;